import { Platform, StyleSheet } from 'react-native';

/** Clubhouse palette — deep club green on warm white, dark cards, Hubot Sans. */
export const palette = {
  base: {
    background: '#F7F7F3',
    card: '#FFFFFF',
    text: '#111111',
    secondary: '#6B7280',
    interactive: '#15803D',
    interactiveStrong: '#15803D',
    interactivePressed: '#166534',
    accent: '#22C55E',
    darkGreen: '#14532D',
    surfaceDark: '#18181B',
    disabled: '#9CA3AF',
    surfaceMuted: '#E8E8E3',
    surfaceSoft: '#EFEFEA',
  },
  success: {
    light: '#DCFCE7',
    DEFAULT: '#15803D',
    dark: '#166534',
  },
  error: {
    light: '#F7C4D3',
    DEFAULT: '#C9164B',
    dark: '#C9164B',
  },
  warning: {
    light: '#F5EA8E',
    DEFAULT: '#ECD407',
    dark: '#EE5328',
  },
} as const;

export const fontFamily = Platform.select({
  web: 'Hubot Sans, "Helvetica Neue", system-ui, sans-serif',
  default: undefined,
});

/** Semantic tokens mapped to the palette for consistent UI usage. */
export const colors = {
  background: palette.base.background,
  backgroundMuted: palette.base.surfaceMuted,
  card: palette.base.card,
  surfaceSubtle: palette.base.background,
  surfaceMuted: palette.base.surfaceMuted,
  surfaceSoft: palette.base.surfaceSoft,
  surfaceDark: palette.base.surfaceDark,
  surfaceDarkElevated: palette.base.surfaceDark,

  text: palette.base.text,
  textSecondary: palette.base.secondary,
  textMuted: palette.base.secondary,
  textOnDark: palette.base.card,
  textOnPrimary: palette.base.card,
  textDisabled: palette.base.disabled,

  interactive: palette.base.interactive,
  interactiveStrong: palette.base.interactiveStrong,
  interactivePressed: palette.base.interactivePressed,
  accent: palette.base.accent,
  premium: palette.success.dark,
  disabled: palette.base.disabled,

  /** Legacy aliases — mapped to the club green accent. */
  purple: palette.base.interactive,
  purpleDark: palette.base.interactivePressed,
  purpleLight: palette.base.secondary,
  purpleMuted: palette.base.surfaceMuted,
  purpleSubtle: palette.base.background,
  purpleDeep: palette.base.interactiveStrong,
  textOnPurple: palette.base.secondary,

  muted: palette.base.secondary,
  border: palette.base.surfaceMuted,
  borderStrong: palette.base.secondary,
  borderSubtle: palette.base.surfaceMuted,
  borderPurple: palette.base.surfaceMuted,

  green: palette.success.DEFAULT,
  pink: palette.base.interactive,
  gold: palette.base.text,

  success: palette.success.DEFAULT,
  successMuted: palette.success.light,
  error: palette.error.DEFAULT,
  errorMuted: palette.error.light,
  warning: palette.warning.DEFAULT,
  warningMuted: palette.warning.light,
};

const shadow = {
  shadowColor: palette.base.text,
  shadowOpacity: 0.08,
  shadowRadius: 18,
  shadowOffset: { width: 0, height: 8 },
  elevation: 4,
};

const shadowSoft = {
  shadowColor: palette.base.text,
  shadowOpacity: 0.06,
  shadowRadius: 12,
  shadowOffset: { width: 0, height: 4 },
  elevation: 2,
};

export const styles = StyleSheet.create({
  app: { flex: 1, height: '100%', backgroundColor: colors.background, position: 'relative', overflow: 'hidden', fontFamily },
  webApp: { overflow: 'visible' },
  statusBanner: {
    backgroundColor: colors.surfaceDark,
    paddingVertical: 10,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    zIndex: 30,
  },
  statusBannerText: { color: '#FFFFFF', flex: 1, fontWeight: '700', fontSize: 13, lineHeight: 18 },
  statusBannerAction: { color: colors.interactive, fontWeight: '900', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.6 },
  appScroll: { flex: 1, minHeight: 0 },
  scroll: { padding: 24, paddingBottom: 248 },
  authScroll: { flexGrow: 1, paddingBottom: 48 },
  welcomeScreen: { flex: 1, justifyContent: 'space-between' },
  welcomeMain: { flex: 1 },
  welcomeTop: { gap: 4 },
  welcomeHeroRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 14 },
  welcomeHeroCopy: { flex: 1 },
  welcomeTitle: { fontSize: 28, lineHeight: 32, color: colors.text, fontWeight: '900' },
  welcomeSubtitle: { marginTop: 6, color: colors.textSecondary, fontSize: 15, lineHeight: 22, fontWeight: '600' },
  welcomeForm: { marginTop: 16 },
  welcomeInput: {
    backgroundColor: colors.card,
    borderRadius: 14,
    paddingHorizontal: 16,
    paddingVertical: 14,
    fontSize: 16,
    marginTop: 10,
    color: colors.text,
    borderWidth: 1,
    borderColor: colors.borderSubtle,
    ...shadowSoft,
  },
  welcomeInputFirst: { marginTop: 0 },
  welcomePrimaryButton: { marginTop: 14, backgroundColor: colors.interactiveStrong, paddingVertical: 14, borderRadius: 16, alignItems: 'center', ...shadow },
  welcomeSecondaryButton: { marginTop: 10, backgroundColor: colors.card, paddingVertical: 14, borderRadius: 16, alignItems: 'center', borderWidth: 1, borderColor: colors.border },
  ratingCardCompact: {
    backgroundColor: colors.card,
    borderRadius: 16,
    paddingHorizontal: 14,
    paddingVertical: 12,
    alignItems: 'center',
    minWidth: 96,
    borderWidth: 1,
    borderColor: colors.border,
    ...shadowSoft,
  },
  ratingLabel: { color: colors.muted, fontWeight: '900', fontSize: 10, letterSpacing: 1.2 },
  ratingValue: { fontSize: 24, lineHeight: 28, fontWeight: '900', color: colors.text, marginTop: 2 },
  successCompact: { color: colors.success, fontWeight: '800', fontSize: 12, marginTop: 2 },
  navHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexShrink: 0 },
  navBack: { width: 44, height: 44, borderRadius: 14, backgroundColor: colors.card, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: colors.border, ...shadowSoft },
  navBackText: { color: colors.text, fontSize: 32, lineHeight: 34, fontWeight: '900' },
  navTitle: { flex: 1, marginHorizontal: 14, color: colors.text, fontSize: 18, fontWeight: '900', textAlign: 'center' },
  navSpacer: { width: 44 },
  heroScreen: { flex: 1, minHeight: 680, justifyContent: 'center' },
  badge: { alignSelf: 'flex-start', backgroundColor: colors.successMuted, color: colors.success, paddingHorizontal: 16, paddingVertical: 8, borderRadius: 18, overflow: 'hidden', fontWeight: '700' },
  heroTitle: { marginTop: 20, fontSize: 54, lineHeight: 56, color: colors.text, fontWeight: '900' },
  heroCopy: { marginTop: 20, color: colors.textSecondary, fontSize: 18, lineHeight: 28 },
  ratingCard: {
    alignSelf: 'flex-start',
    marginTop: 20,
    backgroundColor: colors.card,
    borderRadius: 18,
    padding: 18,
    minWidth: 148,
    borderWidth: 1,
    borderColor: colors.border,
    ...shadow,
  },
  homeBrandMark: { color: colors.text, fontWeight: '900', fontSize: 18, letterSpacing: 1.5, textTransform: 'uppercase', fontFamily },
  brandRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  footerBrand: { alignItems: 'center' },
  homeBrandTagline: { color: colors.muted, fontWeight: '700', fontSize: 13, marginTop: 4 },
  homeLegalFooter: { marginTop: 28, paddingTop: 24, borderTopWidth: 1, borderTopColor: colors.border, alignItems: 'center', gap: 10 },
  homeLegalFooterCompact: { marginTop: 8, paddingTop: 12, borderTopWidth: 0, gap: 6 },
  homeLegalCopy: { color: colors.muted, fontWeight: '600', fontSize: 12, textAlign: 'center', lineHeight: 18 },
  homeLegalLinks: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 8, marginTop: 4 },
  homeLegalLink: { color: colors.purple, fontWeight: '900', fontSize: 12, letterSpacing: 0.4 },
  bigNumber: { fontSize: 26, fontWeight: '900', color: colors.text },
  success: { color: colors.success, fontWeight: '800' },
  primaryButton: { marginTop: 22, backgroundColor: colors.interactiveStrong, paddingVertical: 18, borderRadius: 18, alignItems: 'center', ...shadow },
  primaryButtonInline: { marginTop: 0, flex: 1, paddingVertical: 11 },
  matchResultActions: { gap: 14 },
  primaryButtonText: { color: colors.textOnPrimary, fontWeight: '900', fontSize: 16, textTransform: 'uppercase', letterSpacing: 0.5, fontFamily },
  secondaryButton: { marginTop: 14, backgroundColor: colors.card, paddingVertical: 18, borderRadius: 16, alignItems: 'center', borderWidth: 1, borderColor: colors.border },
  secondaryButtonText: { color: colors.text, fontWeight: '900', fontSize: 16 },
  secondaryMiniButton: { backgroundColor: colors.card, paddingHorizontal: 16, paddingVertical: 11, borderRadius: 18, borderWidth: 1, borderColor: colors.border },
  pageTitle: { fontSize: 38, lineHeight: 40, color: colors.text, fontWeight: '900', textTransform: 'uppercase', marginBottom: 12, fontFamily },
  screenTitleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 },
  screenTitleText: { flex: 1, minWidth: 0, marginBottom: 0 },
  screenTitleActions: { flexDirection: 'row', alignItems: 'center', gap: 10, flexShrink: 0 },
  clubhouseWordmarkClub: { fontStyle: 'italic', fontWeight: '900', color: colors.text, fontFamily },
  purpleText: { color: colors.purple, fontWeight: '900' },
  step: { color: colors.muted, fontWeight: '900', letterSpacing: 3, textTransform: 'uppercase', marginVertical: 10 },
  subtitle: { color: colors.textSecondary, fontSize: 17, marginBottom: 24 },
  formPanel: { backgroundColor: colors.surfaceSubtle, marginHorizontal: -24, padding: 24, gap: 16 },
  authForm: { gap: 16 },
  formErrorText: { color: colors.error, fontWeight: '700', fontSize: 14, marginTop: -4 },
  inputLabel: { color: colors.muted, fontWeight: '900', letterSpacing: 2, marginBottom: 8 },
  fakeInput: { backgroundColor: colors.card, borderWidth: 2, borderColor: colors.borderSubtle, padding: 18, borderRadius: 15, color: colors.text, fontWeight: '700' },
  authInputWrap: { position: 'relative' },
  authInput: { paddingRight: 72 },
  authInputValid: { borderColor: colors.success },
  authInputActions: { position: 'absolute', right: 14, top: 0, bottom: 0, flexDirection: 'row', alignItems: 'center', gap: 10 },
  authInputCheck: { color: colors.success, fontWeight: '900', fontSize: 16 },
  authInputToggle: { color: colors.purple, fontWeight: '800', fontSize: 13 },
  countryPills: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  countryPill: { paddingHorizontal: 14, paddingVertical: 10, borderRadius: 999, borderWidth: 1, borderColor: colors.borderSubtle, backgroundColor: colors.card },
  countryPillActive: { borderColor: colors.interactiveStrong, backgroundColor: colors.surfaceMuted },
  countryPillText: { color: colors.muted, fontWeight: '800', fontSize: 13 },
  countryPillTextActive: { color: colors.text },
  mutedInput: { borderWidth: 0, color: colors.muted },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  logo: { fontSize: 22, color: colors.text, fontWeight: '900', fontFamily },
  logoMark: { width: 40, height: 40 },
  bellButton: {
    width: 44,
    height: 44,
    borderRadius: 14,
    backgroundColor: colors.card,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.border,
    ...shadowSoft,
  },
  bellIcon: { width: 18, height: 18, alignItems: 'center', justifyContent: 'flex-start' },
  bellKnob: { width: 4, height: 4, borderRadius: 2, backgroundColor: colors.purple, marginBottom: 1 },
  bellCup: {
    width: 14,
    height: 9,
    borderTopLeftRadius: 7,
    borderTopRightRadius: 7,
    borderBottomLeftRadius: 2,
    borderBottomRightRadius: 2,
    backgroundColor: colors.purple,
  },
  bellRim: { width: 16, height: 2, borderRadius: 1, backgroundColor: colors.purple, marginTop: 1 },
  bellClapper: { width: 4, height: 4, borderRadius: 2, backgroundColor: colors.purpleDark, marginTop: 1 },
  bellBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    minWidth: 18,
    height: 18,
    paddingHorizontal: 4,
    borderRadius: 999,
    backgroundColor: colors.interactiveStrong,
    borderWidth: 2,
    borderColor: colors.background,
    alignItems: 'center',
    justifyContent: 'center',
  },
  bellBadgeText: { color: colors.textOnPrimary, fontSize: 10, fontWeight: '900', lineHeight: 12, includeFontPadding: false },
  pillRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 18 },
  filterPill: { backgroundColor: colors.card, color: colors.textSecondary, paddingHorizontal: 16, paddingVertical: 10, borderRadius: 18, overflow: 'hidden', fontWeight: '900', fontSize: 12, borderWidth: 1, borderColor: colors.border },
  activePill: { backgroundColor: colors.interactiveStrong, color: colors.textOnPrimary, borderColor: colors.interactiveStrong },
  featureCard: { position: 'relative', backgroundColor: colors.surfaceDark, borderRadius: 22, minHeight: 220, marginBottom: 18, overflow: 'hidden', ...shadow },
  featureCardMedia: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%', zIndex: 0, overflow: 'hidden' },
  featureCardMediaFill: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%' },
  featureCardScrim: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.58)', zIndex: 1 },
  featureCardContent: { position: 'relative', padding: 24, zIndex: 2 },
  featureImage: { width: '100%', height: 120, borderRadius: 14, marginBottom: 12 },
  eventImage: { position: 'relative', width: '100%', height: 160, borderRadius: 12, marginBottom: 10, overflow: 'hidden' },
  communityHeader: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  communityHeaderText: { flex: 1 },
  communityLogo: {
    width: 56,
    height: 56,
    borderRadius: 14,
    overflow: 'hidden',
    backgroundColor: colors.surfaceMuted,
    alignItems: 'center',
    justifyContent: 'center',
  },
  communityLogoImage: {
    width: '100%',
    height: '100%',
  },
  communityLogoText: {
    color: colors.textSecondary,
    fontWeight: '900',
    fontSize: 16,
    lineHeight: 16,
    textAlign: 'center',
    includeFontPadding: false,
  },
  golfClubPhoto: {
    width: '100%',
    height: 148,
    borderRadius: 14,
    overflow: 'hidden',
    backgroundColor: colors.surfaceMuted,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  courseOptionPhoto: {
    width: '100%',
    height: 120,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: colors.surfaceMuted,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  coverPhotoImage: {
    width: '100%',
    height: '100%',
  },
  coverPhotoFallback: {
    color: colors.textSecondary,
    fontWeight: '900',
    fontSize: 22,
    lineHeight: 24,
    textAlign: 'center',
    includeFontPadding: false,
  },
  activeMatchCard: {
    backgroundColor: colors.surfaceDark,
    borderRadius: 24,
    padding: 22,
    marginBottom: 18,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.interactiveStrong,
    ...shadow,
  },
  activeMatchGlow: {
    position: 'absolute',
    top: -30,
    right: -20,
    width: 140,
    height: 140,
    borderRadius: 70,
    backgroundColor: palette.base.darkGreen,
    opacity: 0.08,
  },
  activeMatchHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  activeMatchBadge: {
    color: palette.base.card,
    backgroundColor: colors.interactiveStrong,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    overflow: 'hidden',
    fontWeight: '900',
    fontSize: 11,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
  },
  activeMatchTitle: { color: colors.textOnDark, fontSize: 24, lineHeight: 28, fontWeight: '900', marginBottom: 6 },
  activeMatchSubtitle: { color: colors.textSecondary, fontSize: 15, lineHeight: 20, marginBottom: 16 },
  activeMatchPlayers: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderRadius: 18,
    padding: 14,
    marginBottom: 14,
  },
  activeMatchPlayerBlock: { flex: 1, alignItems: 'center' },
  activeMatchPlayerName: { color: colors.textOnDark, fontWeight: '900', fontSize: 13, marginTop: 8, textAlign: 'center' },
  activeMatchPlayerStatus: { color: colors.textSecondary, fontWeight: '700', fontSize: 11, marginTop: 4, textTransform: 'uppercase' },
  activeMatchVs: {
    color: palette.base.card,
    fontWeight: '900',
    fontSize: 13,
    letterSpacing: 1,
    paddingHorizontal: 10,
  },
  activeMatchAvatar: {
    width: 46,
    height: 46,
    borderRadius: 23,
    backgroundColor: colors.surfaceMuted,
    borderWidth: 2,
    borderColor: colors.textSecondary,
  },
  activeMatchAvatarText: {
    color: colors.textSecondary,
    fontSize: 14,
    lineHeight: 14,
  },
  activeMatchAvatarMuted: {
    backgroundColor: '#2C2C2E',
    borderColor: colors.disabled,
  },
  activeMatchAvatarMutedText: {
    color: colors.disabled,
  },
  activeMatchProgressTrack: {
    height: 8,
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 999,
    overflow: 'hidden',
    marginBottom: 8,
  },
  activeMatchProgressFill: {
    height: 8,
    backgroundColor: palette.base.card,
    borderRadius: 999,
  },
  activeMatchProgressLabel: { color: colors.textSecondary, fontWeight: '800', fontSize: 12, marginBottom: 14, letterSpacing: 0.5 },
  activeMatchCta: {
    backgroundColor: colors.interactive,
    borderRadius: 16,
    paddingVertical: 14,
    alignItems: 'center',
  },
  activeMatchCtaText: { color: colors.textOnPrimary, fontWeight: '900', fontSize: 14, letterSpacing: 0.8, textTransform: 'uppercase' },
  featureLabel: { color: colors.textOnDark, fontWeight: '900', textTransform: 'uppercase', letterSpacing: 1.5 },
  featureTitle: { color: colors.textOnDark, fontSize: 30, lineHeight: 32, fontWeight: '900', marginTop: 16 },
  featureCopy: { color: colors.textOnDark, marginTop: 14, lineHeight: 20, fontWeight: '600' },
  featureButton: { marginTop: 20, backgroundColor: colors.interactive, color: colors.textOnPrimary, alignSelf: 'flex-start', paddingHorizontal: 22, paddingVertical: 12, borderRadius: 12, overflow: 'hidden', fontWeight: '900' },
  sectionHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  sectionTitle: { fontSize: 18, fontWeight: '900', color: colors.text, textTransform: 'uppercase', marginVertical: 14 },
  link: { color: colors.purple, fontWeight: '900' },
  horizontalCards: { flexDirection: 'row', gap: 12 },
  eventCard: { flex: 1, backgroundColor: colors.card, borderRadius: 14, padding: 14, minHeight: 110, borderWidth: 1, borderColor: colors.border, ...shadowSoft },
  tag: { color: colors.purpleDark, fontWeight: '900', backgroundColor: colors.purpleMuted, alignSelf: 'flex-start', padding: 6, borderRadius: 8, overflow: 'hidden' },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
  quickCard: { width: '48%', backgroundColor: colors.card, borderRadius: 14, padding: 16, minHeight: 118, borderWidth: 1, borderColor: colors.border, ...shadowSoft },
  disabledQuickCard: { opacity: 0.45 },
  quickIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceMuted,
    borderWidth: 1,
    borderColor: colors.borderSubtle,
    marginBottom: 10,
  },
  soonText: { color: colors.disabled, fontWeight: '900' },
  cardTitle: { color: colors.text, fontWeight: '900', fontSize: 16 },
  muted: { color: colors.muted, fontWeight: '600', marginTop: 4 },
  podium: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    backgroundColor: colors.surfaceMuted,
    borderRadius: 20,
    paddingVertical: 16,
    paddingHorizontal: 12,
    marginBottom: 18,
    borderWidth: 1,
    borderColor: colors.borderSubtle,
  },
  podiumPlayer: {
    flex: 1,
    flexBasis: 0,
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 4,
  },
  podiumAvatarWrap: {
    padding: 2,
    borderWidth: 2,
    borderColor: colors.borderStrong,
    borderRadius: 999,
  },
  podiumWinnerRing: {
    borderColor: colors.purple,
  },
  podiumName: {
    color: colors.text,
    fontWeight: '900',
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
    width: '100%',
    minHeight: 40,
  },
  podiumPoints: {
    color: colors.purple,
    fontWeight: '900',
    fontSize: 14,
    lineHeight: 18,
    textAlign: 'center',
  },
  podiumRecord: {
    color: colors.muted,
    fontWeight: '700',
    fontSize: 11,
    lineHeight: 15,
    textAlign: 'center',
    width: '100%',
  },
  playerRecord: {
    color: colors.muted,
    fontWeight: '600',
    marginTop: 4,
    fontSize: 12,
    lineHeight: 16,
  },
  rowCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: colors.card, padding: 14, borderRadius: 14, marginBottom: 12, gap: 12, borderWidth: 1, borderColor: colors.border, ...shadowSoft },
  leaderboardPlayer: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 12, minWidth: 0 },
  activeRow: { borderColor: colors.interactive, borderWidth: 1, backgroundColor: 'rgba(21, 128, 61, 0.06)' },
  rowContent: { flex: 1 },
  rankNumber: { color: colors.purple, fontWeight: '900', width: 24 },
  points: { color: colors.purple, fontWeight: '900', fontSize: 18 },
  smallButton: { color: colors.purple, fontWeight: '900' },
  avatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
    overflow: 'hidden',
    backgroundColor: colors.surfaceMuted,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    color: colors.textSecondary,
    fontWeight: '900',
    fontSize: 16,
    lineHeight: 16,
    textAlign: 'center',
    includeFontPadding: false,
  },
  avatarImage: {
    width: '100%',
    height: '100%',
  },
  avatarUploadingMask: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  smallAvatar: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: colors.surfaceMuted,
  },
  smallAvatarText: {
    color: colors.textSecondary,
    fontSize: 13,
    lineHeight: 13,
  },
  largeAvatar: {
    width: 72,
    height: 72,
    borderRadius: 36,
  },
  largeAvatarText: {
    fontSize: 22,
    lineHeight: 22,
  },
  searchInput: { backgroundColor: colors.card, borderRadius: 14, padding: 16, fontSize: 16, marginVertical: 16, borderWidth: 1, borderColor: colors.border, ...shadowSoft },
  scanBox: { borderWidth: 2, borderColor: colors.borderStrong, borderStyle: 'dashed', borderRadius: 18, alignItems: 'center', justifyContent: 'center', minHeight: 150, marginBottom: 18 },
  scanIcon: { color: colors.purple, fontSize: 30, textAlign: 'center', fontWeight: '900' },
  pillButton: { backgroundColor: colors.interactiveStrong, paddingHorizontal: 16, paddingVertical: 11, borderRadius: 18 },
  pillButtonText: { color: colors.textOnPrimary, fontWeight: '900' },
  matchPair: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', backgroundColor: colors.card, padding: 18, borderRadius: 16, marginVertical: 18, borderWidth: 1, borderColor: colors.border, ...shadowSoft },
  vs: { backgroundColor: colors.surfaceMuted, color: colors.muted, fontWeight: '900', paddingHorizontal: 14, paddingVertical: 8, borderRadius: 16, overflow: 'hidden' },
  holes: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginVertical: 14 },
  holeChip: { backgroundColor: colors.card, color: colors.text, borderRadius: 10, padding: 12, overflow: 'hidden', fontWeight: '900', textAlign: 'center', borderWidth: 1, borderColor: colors.border },
  futureHole: { borderStyle: 'dashed', color: colors.muted },
  completedHole: { borderColor: colors.borderStrong },
  activeHole: { backgroundColor: colors.interactiveStrong, color: colors.textOnPrimary, borderColor: colors.interactiveStrong },
  scoreCard: { backgroundColor: colors.card, borderRadius: 20, padding: 20, borderWidth: 1, borderColor: colors.border, ...shadowSoft },
  scoreLine: { flexDirection: 'row', justifyContent: 'space-between', gap: 16 },
  scoreStepper: { flex: 1, alignItems: 'center' },
  roundControl: { backgroundColor: colors.surfaceMuted, width: 36, height: 36, borderRadius: 18, overflow: 'hidden', textAlign: 'center', paddingTop: 8, fontWeight: '900' },
  scoreNumber: { fontSize: 44, color: colors.text, fontWeight: '900', marginHorizontal: 12 },
  scoreNumberEmpty: { color: colors.muted },
  scoreFromOpponent: { color: colors.muted, fontSize: 12, fontWeight: '700', marginTop: 4, textAlign: 'center' },
  discrepancyCard: { marginTop: 12, gap: 8 },
  discrepancyChoices: { flexDirection: 'row', gap: 8, marginTop: 8 },
  discrepancyChoice: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingVertical: 12, borderRadius: 12, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.card },
  discrepancyChoiceActive: { borderColor: colors.interactiveStrong, backgroundColor: colors.purpleSubtle },
  disputedHole: { borderColor: colors.error },
  row: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  pageHeaderRow: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 },
  winPill: { backgroundColor: colors.successMuted, paddingHorizontal: 14, paddingVertical: 8, borderRadius: 18, overflow: 'hidden' },
  winPillText: { color: colors.success, fontWeight: '900' },
  lossPill: { backgroundColor: colors.errorMuted, paddingHorizontal: 14, paddingVertical: 8, borderRadius: 18, overflow: 'hidden' },
  lossPillText: { color: colors.error, fontWeight: '900' },
  drawPill: { backgroundColor: colors.purpleMuted, paddingHorizontal: 14, paddingVertical: 8, borderRadius: 18, overflow: 'hidden' },
  drawPillText: { color: colors.purpleDark, fontWeight: '900' },
  resultCard: { backgroundColor: colors.card, borderRadius: 20, padding: 18, marginBottom: 16, borderWidth: 1, borderColor: colors.border, ...shadowSoft },
  resultLine: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 },
  resultScoreBlock: { alignItems: 'center', gap: 2 },
  resultScore: { color: colors.text, fontSize: 34, textAlign: 'center', fontWeight: '900' },
  vsLabel: { color: colors.muted, fontWeight: '900', letterSpacing: 2 },
  statGrid: { backgroundColor: colors.card, borderRadius: 20, padding: 18, marginBottom: 16, flexDirection: 'row', justifyContent: 'space-around', borderWidth: 1, borderColor: colors.border, ...shadowSoft },
  stat: { alignItems: 'center', flex: 1, paddingHorizontal: 4 },
  statCompact: { justifyContent: 'center', minHeight: 56 },
  statValue: { color: colors.purple, fontSize: 22, fontWeight: '900', textAlign: 'center' },
  statValueCompact: { fontSize: 14, lineHeight: 18 },
  statValueAccent: { color: colors.success },
  statValueAccentLoss: { color: colors.error },
  statLabel: { color: colors.muted, fontSize: 11, letterSpacing: 1.5, fontWeight: '900', marginTop: 6, textAlign: 'center' },
  scorecardTable: { backgroundColor: colors.card, borderRadius: 20, padding: 18, marginBottom: 16, borderWidth: 1, borderColor: colors.border, ...shadowSoft },
  scorecardRow: { flexDirection: 'row', alignItems: 'center', marginVertical: 4 },
  scorecardRowAccent: { backgroundColor: colors.purpleSubtle, borderRadius: 12, paddingVertical: 8 },
  scorecardLabel: { width: 40, color: colors.text, fontWeight: '900', letterSpacing: 1 },
  scorecardLabelAccent: { color: colors.purpleDark },
  scorecardValues: { flex: 1, flexDirection: 'row', justifyContent: 'space-between' },
  scorecardCell: { flex: 1, color: colors.text, fontWeight: '900', textAlign: 'center', fontVariant: ['tabular-nums'] },
  scorecardCellAccent: { color: colors.purpleDark },
  scorecardCellCompact: { fontSize: 12 },
  profileCard: { backgroundColor: colors.card, borderRadius: 20, padding: 24, alignItems: 'center', marginBottom: 16, borderWidth: 1, borderColor: colors.border, ...shadowSoft },
  profileHeaderRow: { flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, marginBottom: 18 },
  profileHeroCard: {
    backgroundColor: colors.surfaceDark,
    borderRadius: 28,
    padding: 18,
    gap: 16,
    marginBottom: 12,
    borderWidth: 0,
    position: 'relative',
    overflow: 'hidden',
  },
  profileHeroTop: { flexDirection: 'row', alignItems: 'center', gap: 14 },
  profileHeroCopy: { flex: 1, gap: 4 },
  profileHeroName: { color: '#FFFFFF', fontSize: 22, lineHeight: 26, fontWeight: '900', fontFamily },
  profileHeroClub: { color: '#A3A3A3', fontSize: 14, fontWeight: '600' },
  profileHeroPills: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8 },
  profileOutlinePill: {
    minHeight: 33,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    overflow: 'hidden',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.45)',
  },
  profileOutlinePillText: {
    color: '#FFFFFF',
    fontWeight: '800',
    fontSize: 12,
    lineHeight: 16,
    includeFontPadding: false,
    textAlign: 'center',
    textAlignVertical: 'center',
  },
  profileGreenPillWrap: {
    minHeight: 33,
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: 999,
    overflow: 'hidden',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.premium,
  },
  profileGreenPillText: {
    color: '#FFFFFF',
    fontWeight: '800',
    fontSize: 12,
    lineHeight: 16,
    includeFontPadding: false,
    textAlign: 'center',
    textAlignVertical: 'center',
    zIndex: 1,
  },
  profileGreenPill: {
    color: '#FFFFFF',
    backgroundColor: colors.premium,
    borderWidth: 0,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    overflow: 'hidden',
    fontWeight: '800',
    fontSize: 12,
  },
  profileStatsBar: {
    backgroundColor: colors.interactive,
    borderRadius: 22,
    paddingVertical: 18,
    paddingHorizontal: 8,
    marginBottom: 16,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 0,
  },
  profileStat: { flex: 1, flexDirection: 'row', alignItems: 'center' },
  profileStatDivider: { width: 1, height: 36, backgroundColor: 'rgba(255,255,255,0.45)', marginRight: 0 },
  profileStatInner: { flex: 1, alignItems: 'center' },
  profileStatValue: { color: '#FFFFFF', fontSize: 22, fontWeight: '900', fontFamily },
  profileStatLabel: { color: '#FFFFFF', fontSize: 10, letterSpacing: 1.2, fontWeight: '800', marginTop: 4, textTransform: 'uppercase' },
  profileName: { color: colors.text, fontSize: 20, fontWeight: '900', marginTop: 12 },
  avatarEditHint: { color: colors.muted, fontSize: 12, marginTop: 8 },
  avatarStatusError: { color: colors.error, fontSize: 12, marginTop: 8, fontWeight: '600' },
  avatarStatusOk: { color: colors.interactiveStrong, fontSize: 12, marginTop: 8, fontWeight: '700' },
  pillLine: { flexDirection: 'row', gap: 8, marginTop: 16 },
  handicapColumn: { alignItems: 'flex-end', gap: 8 },
  handicapValue: { color: colors.text, fontWeight: '900', fontSize: 18, lineHeight: 22 },
  handicapValueInline: { color: colors.text, fontWeight: '900' },
  handicapProfileBlock: { width: '100%', alignItems: 'center' },
  handicapCourseLabel: { color: colors.muted, fontSize: 12, fontWeight: '600', marginTop: 8, textAlign: 'center' },
  lightPill: { color: colors.purple, backgroundColor: colors.purpleMuted, paddingHorizontal: 12, paddingVertical: 8, borderRadius: 16, overflow: 'hidden', fontWeight: '900' },
  goldPill: { color: colors.purpleDark, backgroundColor: colors.purpleSubtle, paddingHorizontal: 12, paddingVertical: 8, borderRadius: 16, overflow: 'hidden', fontWeight: '900' },
  resultChips: { flexDirection: 'row', flexWrap: 'nowrap', backgroundColor: colors.surfaceSoft, borderRadius: 22, padding: 12, gap: 8, marginBottom: 16, borderWidth: 0 },
  resultChip: { flex: 1, height: 40, borderRadius: 12, overflow: 'hidden', alignItems: 'center', justifyContent: 'center', minWidth: 0, borderWidth: 0 },
  resultChipWin: { backgroundColor: colors.successMuted },
  resultChipLoss: { backgroundColor: '#F7C4D3' },
  resultChipDraw: { backgroundColor: colors.surfaceMuted },
  resultChipText: { fontWeight: '900', fontSize: 14, lineHeight: 18, textAlign: 'center', includeFontPadding: false },
  resultChipTextWin: { color: colors.interactive },
  resultChipTextLoss: { color: '#C9164B' },
  resultChipTextDraw: { color: colors.text },
  resultRowBadge: { width: 40, height: 40, borderRadius: 12, alignItems: 'center', justifyContent: 'center', borderWidth: 0 },
  profileMatchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surfaceSoft,
    padding: 14,
    borderRadius: 18,
    marginBottom: 12,
    gap: 12,
    borderWidth: 0,
  },
  performanceRow: {
    backgroundColor: colors.surfaceSoft,
    borderRadius: 18,
    padding: 16,
    marginBottom: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderWidth: 0,
  },
  performanceLabel: { color: colors.textSecondary, fontWeight: '600', fontSize: 14 },
  performanceValue: { color: colors.text, fontWeight: '900', fontSize: 16 },
  memberCard: { position: 'relative', overflow: 'hidden', backgroundColor: colors.surfaceDarkElevated, borderRadius: 26, padding: 18, marginBottom: 16, borderWidth: 1, borderColor: 'rgba(255,255,255,0.14)', ...shadow },
  memberCardGlowTop: { position: 'absolute', top: -60, right: -38, width: 160, height: 160, borderRadius: 80, backgroundColor: 'rgba(255,255,255,0.09)' },
  memberCardGlowBottom: { position: 'absolute', bottom: -72, left: -46, width: 210, height: 130, borderRadius: 80, backgroundColor: 'rgba(255,255,255,0.045)' },
  memberCardPressable: { gap: 20 },
  memberCardTop: { minHeight: 32, paddingRight: 46, justifyContent: 'center' },
  memberCardBrand: { color: 'rgba(255,255,255,0.72)', fontSize: 11, fontWeight: '900', letterSpacing: 2.4 },
  memberCardHero: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, paddingRight: 8 },
  memberCardIdentity: { flex: 1, gap: 5 },
  memberCardName: { color: colors.textOnDark, fontSize: 24, lineHeight: 29, fontWeight: '900', flexShrink: 1 },
  memberCardMeta: { color: 'rgba(255,255,255,0.68)', fontSize: 13, lineHeight: 17, fontWeight: '700', flexShrink: 1 },
  memberCardFooter: { flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', gap: 14 },
  memberCardFooterLeft: { flex: 1, gap: 10 },
  memberCardStats: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  memberCardStat: { minWidth: 70, paddingHorizontal: 12, paddingVertical: 9, borderRadius: 16, backgroundColor: 'rgba(255,255,255,0.11)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)' },
  memberCardStatLabel: { color: 'rgba(255,255,255,0.58)', fontSize: 10, fontWeight: '900', letterSpacing: 1.2 },
  memberCardStatValue: { color: colors.textOnDark, fontSize: 18, lineHeight: 22, fontWeight: '900', marginTop: 2 },
  memberCardMemberPill: { alignSelf: 'center', paddingHorizontal: 12, paddingVertical: 8, borderRadius: 999, backgroundColor: colors.textOnDark },
  memberCardMemberText: { color: colors.surfaceDarkElevated, fontSize: 10, fontWeight: '900', letterSpacing: 1.2 },
  memberCardHandicap: { color: colors.muted, fontSize: 12, fontWeight: '600', marginTop: 2, flexShrink: 1 },
  memberCardBadge: { color: colors.purpleDark, fontWeight: '800', fontSize: 10, letterSpacing: 0.8, marginTop: 2 },
  cardHandle: { color: 'rgba(255,255,255,0.86)', fontWeight: '900', fontSize: 16, lineHeight: 20, flexShrink: 1 },
  cardLink: { color: colors.muted, fontWeight: '600', fontSize: 13, marginTop: 6 },
  cardShareButton: {
    position: 'absolute',
    top: 14,
    right: 14,
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#FFFFFF',
    zIndex: 2,
  },
  cardShareIcon: { color: '#FFFFFF', fontWeight: '900', fontSize: 16, lineHeight: 18 },
  homeCardFooter: { flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12 },
  homeCardPills: { flex: 1, flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8, paddingRight: 4 },
  homeCardPressable: { gap: 16, paddingRight: 40 },
  homeCardQrFace: { minHeight: 220, alignItems: 'center', justifyContent: 'center' },
  goldText: { color: colors.purpleDark, fontWeight: '900', letterSpacing: 1 },
  qrWrap: { alignSelf: 'center', alignItems: 'center', justifyContent: 'center', width: 184, height: 184, marginVertical: 18, borderRadius: 16, backgroundColor: colors.card, borderWidth: 1, borderColor: colors.borderSubtle, ...shadowSoft },
  qrWrapCompact: { alignItems: 'center', justifyContent: 'center', width: 88, height: 88, borderRadius: 16, backgroundColor: '#FFFFFF', borderWidth: 0 },
  qrImage: { width: 168, height: 168, borderRadius: 8 },
  moduleCard: { backgroundColor: colors.card, borderRadius: 16, padding: 18, marginBottom: 14, gap: 8, borderWidth: 1, borderColor: colors.border, ...shadowSoft },
  inviteCard: { backgroundColor: colors.card, borderRadius: 16, padding: 18, marginBottom: 16, borderWidth: 1, borderColor: colors.border, ...shadowSoft },
  inviteCardHeader: { flexDirection: 'row', alignItems: 'flex-start', gap: 12 },
  inviteCardIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceMuted,
    borderWidth: 1,
    borderColor: colors.borderSubtle,
  },
  inviteCardCopy: { flex: 1, gap: 4 },
  inviteCardButton: {
    marginTop: 14,
    backgroundColor: colors.surfaceMuted,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 8,
    borderWidth: 1,
    borderColor: colors.borderSubtle,
  },
  inviteCardButtonText: { color: colors.text, fontWeight: '900', fontSize: 15 },
  settingsGroup: { backgroundColor: colors.card, borderRadius: 14, marginBottom: 22, overflow: 'hidden', borderWidth: 1, borderColor: colors.border },
  settingsRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 16, borderBottomWidth: 1, borderBottomColor: colors.border },
  danger: { color: colors.error, padding: 16, fontWeight: '900' },
  cancelMatchLink: { color: colors.error, fontWeight: '900', textAlign: 'center', marginBottom: 18, textTransform: 'uppercase', letterSpacing: 0.5 },
  cancelMatchButton: { marginTop: 12, alignItems: 'center', paddingVertical: 12 },
  tabs: {
    backgroundColor: 'rgba(255, 255, 255, 0.86)',
    borderRadius: 30,
    paddingHorizontal: 4,
    height: 81,
    flexDirection: 'row',
    justifyContent: 'space-around',
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.92)',
    shadowColor: '#111111',
    shadowOpacity: 0.12,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 10 },
    elevation: 10,
  },
  tabsWrap: {
    position: 'absolute',
    left: 16,
    right: 16,
    bottom: 0,
    zIndex: 20,
  },
  tabItem: { flex: 1, minWidth: 48, height: 81, alignItems: 'center', justifyContent: 'center', gap: 4, borderRadius: 30, overflow: 'visible' },
  tabItemActive: {
    backgroundColor: '#FFFFFF',
    shadowColor: '#111111',
    shadowOpacity: 0.06,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  tabIconWrap: { width: 28, height: 28, alignItems: 'center', justifyContent: 'center', overflow: 'visible' },
  tabIconWrapActive: {},
  tabText: { color: '#2F2F2C', fontWeight: '700', fontSize: 9, lineHeight: 12, textTransform: 'uppercase', letterSpacing: 0.4, fontFamily },
  activeTab: { color: colors.interactive },
  notificationRow: { backgroundColor: colors.card, borderRadius: 16, padding: 14, marginBottom: 12, borderWidth: 1, borderColor: colors.border, ...shadowSoft },
  notificationRowUnread: { backgroundColor: colors.surfaceMuted, borderWidth: 1, borderColor: colors.borderSubtle },
  notificationRowRead: { opacity: 0.72 },
  notificationRowMain: { flexDirection: 'row', alignItems: 'center', gap: 12, flex: 1 },
  notificationRowContent: { flexDirection: 'row', alignItems: 'center', gap: 12, flex: 1 },
  notificationDismissButton: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.surfaceMuted },
  notificationDismissText: { color: colors.muted, fontSize: 16, fontWeight: '700', lineHeight: 18 },
  notificationHeaderRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', minHeight: 44, marginBottom: 16 },
  notificationReadAllButton: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 999, backgroundColor: colors.purpleSubtle },
  notificationReadAllButtonDisabled: { opacity: 0.35 },
  notificationReadAllText: { color: colors.purple, fontWeight: '800', fontSize: 13 },
  notificationIconWrap: { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.surfaceMuted },
  notificationIconWrapMatchplay: { backgroundColor: colors.purpleMuted },
  notificationIconWrapResult: { backgroundColor: colors.warningMuted },
  notificationIconWrapCancelled: { backgroundColor: colors.errorMuted },
  notificationIconWrapFriend: { backgroundColor: colors.purpleSubtle },
  notificationIconWrapDuel: { backgroundColor: colors.surfaceMuted },
  notificationIconWrapBooking: { backgroundColor: colors.purpleSubtle },
  notificationIconWrapJoin: { backgroundColor: colors.surfaceMuted },
  notificationIcon: { fontSize: 20, lineHeight: 22, textAlign: 'center', includeFontPadding: false },
  notificationContent: { flex: 1 },
  notificationTitle: { color: colors.text, fontWeight: '900', fontSize: 15 },
  notificationTitleRead: { color: colors.muted, fontWeight: '700' },
  notificationBody: { color: colors.muted, fontWeight: '600', marginTop: 4, lineHeight: 18 },
  notificationUnreadDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: colors.accent, marginRight: 8 },
  notificationActions: { flexDirection: 'row', gap: 10, marginTop: 10, marginLeft: 56 },
  courseOption: {
    backgroundColor: colors.card,
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    borderWidth: 2,
    borderColor: 'transparent',
    ...shadowSoft,
  },
  courseOptionActive: { borderColor: colors.interactive, backgroundColor: colors.surfaceMuted },
  courseOptionHeader: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 },
  courseOptionTitle: { color: colors.text, fontWeight: '900', fontSize: 16, flex: 1 },
  courseOptionMeta: { color: colors.muted, fontWeight: '600', marginTop: 4 },
  courseOptionHint: { color: colors.textSecondary, fontWeight: '600', fontSize: 12, lineHeight: 17, marginTop: 8 },
  courseHoleBadge: {
    color: colors.textOnPrimary,
    backgroundColor: colors.interactiveStrong,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    overflow: 'hidden',
    fontWeight: '900',
    fontSize: 12,
    textTransform: 'uppercase',
  },
  courseHoleBadgeShort: { backgroundColor: colors.interactiveStrong },
  courseHoleBadgeFull: { backgroundColor: colors.surfaceDark },
  courseOptionBadges: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 8 },
  courseOptionBadge: {
    alignSelf: 'flex-start',
    color: colors.purple,
    backgroundColor: colors.purpleMuted,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    overflow: 'hidden',
    fontWeight: '900',
    fontSize: 11,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  courseHelpCard: { backgroundColor: colors.surfaceMuted, borderRadius: 16, padding: 14, borderWidth: 1, borderColor: colors.borderSubtle },
  courseHelpTitle: { color: colors.text, fontWeight: '900', fontSize: 14 },
  courseHelpText: { color: colors.textSecondary, fontWeight: '600', lineHeight: 18, marginTop: 4 },
  selectedCourseHeader: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 },
  selectedHoleBadge: {
    color: colors.textOnPrimary,
    backgroundColor: colors.interactiveStrong,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 999,
    overflow: 'hidden',
    fontWeight: '900',
    fontSize: 12,
    textTransform: 'uppercase',
  },
  courseSelectOuter: { flex: 1, minHeight: 0, paddingHorizontal: 24, paddingTop: 8 },
  courseSelectSubtitle: { color: colors.textSecondary, fontSize: 15, fontWeight: '600', marginBottom: 12, flexShrink: 0 },
  courseSelectSearch: {
    backgroundColor: colors.card,
    borderRadius: 14,
    padding: 14,
    fontSize: 16,
    marginBottom: 12,
    flexShrink: 0,
    borderWidth: 1,
    borderColor: colors.border,
    ...shadowSoft,
  },
  courseSelectScreen: { flex: 1, minHeight: 0, flexDirection: 'column' },
  courseListViewport: { flex: 1, minHeight: 0 },
  courseListScroll: { flex: 1, minHeight: 0 },
  courseListContent: { paddingBottom: 88 },
  courseStartPanel: {
    backgroundColor: colors.card,
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    flexShrink: 0,
    borderWidth: 2,
    borderColor: colors.interactive,
    ...shadowSoft,
  },
  courseStartPanelTitle: { color: colors.text, fontWeight: '900', fontSize: 16 },
  courseStartHint: { color: colors.textSecondary, fontWeight: '600', fontSize: 12, lineHeight: 17, marginTop: 8 },
  teeRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 12 },
  teeChip: {
    backgroundColor: colors.surfaceMuted,
    color: colors.text,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 999,
    overflow: 'hidden',
    fontWeight: '900',
  },
  teeChipActive: { backgroundColor: colors.interactiveStrong, color: colors.textOnPrimary },
  landingScroll: { gap: 20 },
  landingHero: { gap: 8 },
  landingTagline: { color: colors.textSecondary, fontSize: 15, fontWeight: '600' },
  landingTitle: { fontSize: 32, lineHeight: 36, color: colors.text, fontWeight: '900', marginTop: 8 },
  landingSubtitle: { color: colors.textSecondary, fontSize: 16, lineHeight: 24, fontWeight: '600' },
  landingMockCard: {
    backgroundColor: colors.surfaceDark,
    borderRadius: 18,
    padding: 18,
    gap: 6,
    ...shadowSoft,
  },
  landingMockValue: { fontSize: 28, lineHeight: 32, fontWeight: '900', color: colors.textOnDark, marginTop: 2 },
  landingMockName: { color: colors.textOnDark, fontWeight: '900', fontSize: 18, lineHeight: 24, marginTop: 8 },
  landingMockMeta: { color: colors.textOnPurple, fontWeight: '600', fontSize: 14, lineHeight: 20 },
  landingFeatures: { gap: 10 },
  landingFeatureItem: { color: colors.text, fontWeight: '600', fontSize: 15, lineHeight: 22 },
  landingActions: { gap: 10, marginTop: 4, marginBottom: 40 },
  landingBackLink: { alignItems: 'center', paddingVertical: 12 },
  landingBackLinkText: { color: colors.purple, fontWeight: '800', fontSize: 14 },
  storeButtons: { gap: 10 },
  storeButtonsLabel: { color: colors.text, fontWeight: '900', fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.8 },
  storeButtonsRow: { flexDirection: 'row', gap: 10 },
  pwaInstallCard: {
    marginTop: 4,
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.card,
    gap: 10,
  },
  pwaInstallTitle: { color: colors.text, fontWeight: '900', fontSize: 15, textTransform: 'uppercase', letterSpacing: 0.6 },
  pwaInstallIntro: { color: colors.textSecondary, fontSize: 14, lineHeight: 20 },
  pwaInstallPlatform: { color: colors.purple, fontWeight: '800', fontSize: 13, marginTop: 2 },
  pwaInstallSteps: { gap: 10, marginTop: 4 },
  pwaInstallStepRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  pwaInstallStepNumber: {
    width: 22,
    height: 22,
    borderRadius: 11,
    overflow: 'hidden',
    backgroundColor: colors.purple,
    color: '#fff',
    fontWeight: '800',
    fontSize: 12,
    textAlign: 'center',
    lineHeight: 22,
  },
  pwaInstallStepText: { flex: 1, color: colors.text, fontSize: 14, lineHeight: 20 },
  storeButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: colors.card,
    borderRadius: 14,
    padding: 14,
    borderWidth: 1,
    borderColor: colors.border,
    opacity: 0.88,
    ...shadowSoft,
  },
  storeButtonPlatform: { color: colors.text, fontSize: 22, fontWeight: '900', width: 24, textAlign: 'center' },
  storeButtonCopy: { flex: 1, gap: 2 },
  storeButtonSoon: { color: colors.textSecondary, fontWeight: '700', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6 },
  storeButtonLabel: { color: colors.text, fontWeight: '900', fontSize: 15 },
  legalPage: { gap: 16, paddingBottom: 24 },
  legalPageTitle: { color: colors.text, fontWeight: '900', fontSize: 28, lineHeight: 32 },
  legalPageBody: { color: colors.textSecondary, fontWeight: '600', fontSize: 16, lineHeight: 24 },
});
